import Chat16 from '@carbon/icons-react/es/chat/16';
import Gift16 from '@carbon/icons-react/es/gift/16';
import Notification16 from '@carbon/icons-react/es/notification/16';
import { useSize } from 'ahooks';
import { Image, Tooltip } from 'antd';
import React, { FC, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useHistory, useLocation } from 'react-router-dom';
import RocketlaneLogo from 'assets/rocketlane_logo_64px.png';
import { FCWidget } from 'components/atoms/freshchat-widget';
import { NewTaskModal } from 'components/molecules/new-task-modal';
import { KeyMaps, useShortcut } from 'hooks/use-shorcut';
import { useAccountDataQuery, useNotificationDataQuery } from 'queries/account';
import { getDevEnv } from 'utils/is-localhost';
import styles from '../../molecules/notifications-badge/notifications-badge.module.css';
import { useNotificationsDrawer } from '../notifications/drawer/hash-based-notifications';
import {
  Badge,
  Button,
  ButtonWrapper,
  Logo,
  NotificationsButtonWrapper,
  PrimaryMenu,
  Sidebar,
} from './left-nav.style';
import { usePrimaryMenuItems } from './primary-menus';
import { Profile } from './profile';
import { getMatchedMenuItem } from './utils';

const LogoHeight = 48;

interface LeftNavProps {
  logo?: string;
  // setNotificationsDrawerVisibility: any;
}
export const LeftNav: FC<LeftNavProps> = ({
  logo,
  // setNotificationsDrawerVisibility,
}) => {
  const { pathname } = useLocation();
  const primaryMenuItems = usePrimaryMenuItems();
  const { isLocal } = getDevEnv();

  const selected = useMemo(
    () =>
      (getMatchedMenuItem(primaryMenuItems, pathname) ?? primaryMenuItems[0])
        ?.key,
    [pathname, primaryMenuItems],
  );
  const history = useHistory();

  useShortcut(KeyMaps.Home, () => {
    return history.push('/');
  });
  useShortcut(KeyMaps.Projects, () => {
    return history.push('/projects');
  });
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);

  useShortcut(KeyMaps.NewTask, (e) => {
    e.preventDefault();
    setShowNewTaskModal(true);
  });

  const bottomMenusWrapperRef = useRef<HTMLDivElement>(null);
  const { height: windowHeight = 0 } = useSize(window.document.body);
  const { height: bottomMenuHeight = 0 } = useSize(bottomMenusWrapperRef);
  const availableSpaceForPrimaryMenus =
    windowHeight - bottomMenuHeight - LogoHeight;
  const [[drawerState], show, hide] = useNotificationsDrawer();
  const { data: notificationData } = useNotificationDataQuery();
  const { data: accountData } = useAccountDataQuery();
  // On page load, TO check if any pending notifications are there use the newNotificationAvailable property in the accountData
  // Otherwise, use the pendingNotificationsCount from the notificationData
  // notificationData cache will be updated through websocket.
  const hasPending =
    !!notificationData?.pendingNotifications ||
    accountData?.newNotificationAvailable;

  return (
    <Sidebar>
      <Link to="/">
        <Logo>
          <Image
            width={32}
            height={32}
            preview={false}
            src={logo || RocketlaneLogo}
            title="logo"
            alt="logo"
          />
        </Logo>
      </Link>
      <NewTaskModal
        isVisible={showNewTaskModal}
        onCancel={() => setShowNewTaskModal(false)}
        trigger={() => <></>}
      />
      <NotificationsButtonWrapper>
        <Tooltip placement="right" title="Notifications">
          <Button
            shape="circle"
            size="small"
            icon={
              <Badge
                text={<Notification16 />}
                pending={hasPending ? 1 : 0}
                width={hasPending ? '30px' : undefined}
                className={styles.home_badge}
                titleClassName={styles.home_title}
              />
            }
            onClick={() => {
              if (drawerState === 'show') {
                hide();
              } else {
                show();
              }
            }}
          />
        </Tooltip>
      </NotificationsButtonWrapper>
      <PrimaryMenu
        items={primaryMenuItems}
        selectedKey={selected}
        availableSpace={availableSpaceForPrimaryMenus}
      />
      <ButtonWrapper id="beamer-icon">
        <Tooltip
          placement="right"
          title={<FormattedMessage id="menu.label.announcements" />}
        >
          <Button shape="circle" size="small" icon={<Gift16 />} />
        </Tooltip>
      </ButtonWrapper>
      <div ref={bottomMenusWrapperRef}>
        {!isLocal ? (
          <ButtonWrapper>
            <FCWidget
              render={(openFCWidget) => {
                return (
                  <Tooltip
                    placement="right"
                    title={<FormattedMessage id="menu.label.chat" />}
                  >
                    <Button
                      icon={<Chat16 />}
                      size="small"
                      shape="circle"
                      onClick={openFCWidget}
                    />
                  </Tooltip>
                );
              }}
            />
          </ButtonWrapper>
        ) : null}

        <ButtonWrapper data-cy="sider.profile.menu">
          <Profile />
        </ButtonWrapper>
      </div>
    </Sidebar>
  );
};
